/**
 * Does the protocol TEXT say the study collects race or ethnicity, when the protocol reading
 * did not store it? (backlog 21 (bbb), 2026-10-20)
 *
 * Measured on Clinical Dev: a fresh build of a real NIH-funded protocol stored no race or
 * ethnicity under demographicsFields, so the house Demographics layout's race and ethnicity
 * questions were gated out and no check said so. The protocol lists both, with the OMB
 * categories, in its enrolment table ("Race: American Indian or Alaskan Native 0 Asian 0 ...").
 *
 * Three kinds of evidence, each precise enough to act on without a model:
 *   1. a category LIST: three or more of the OMB minimum race categories within a short span
 *      (race), or the "Hispanic or Latino" / "Not Hispanic or Latino" pair (ethnicity). A list
 *      of the categories is what an enrolment table, a CRF appendix or a demographics section
 *      prints, and nothing else does;
 *   2. a COLLECTION sentence: race (or ethnicity) in the same sentence as collect / record /
 *      capture / obtain / self-report / document, or in a demographics list ("Demographic data
 *      (age, sex, race and ethnicity)"), and neither negated nor "regardless of";
 *   3. NIH FUNDING (nihFunding): NIH requires every funded clinical study to report enrolment by
 *      the OMB race and ethnicity categories (NIH Inclusion policy), so the study must collect them.
 *
 * Nothing here invents a list. The categories returned are the protocol's own words; when the
 * protocol names none, the form's list comes from the order of authority as before (sponsor,
 * protocol, CDISC CT, which carries the OMB categories).
 */

export type RaceTopic = "race" | "ethnicity";

export interface RaceEvidence {
  /** A short quote of the protocol text the evidence rests on. */
  quote: string;
  /** The protocol's own category labels, in its order (empty for a sentence). */
  categories: string[];
}

/** The OMB 1997 minimum race categories, with the spellings protocols use. */
const OMB_RACE: Array<{ re: RegExp }> = [
  { re: /American Indian (?:or|and|\/) Alaska(?:n)? Native/i },
  { re: /\bAsian\b(?!\s+(?:or|and)\s+Pacific)/i },
  { re: /Black (?:or|and|\/) African[- ]American/i },
  { re: /Native Hawaiian (?:or|and|\/) (?:Other )?Pacific Islander/i },
  { re: /\bWhite\b(?!\s+(?:blood|cells?|matter|coat|count))/i },
];
const SPAN = 700;

const RACE_WORD = /\brace\b|\bracial\b/i;
const ETH_WORD = /\bethnic(?:ity|ities)?\b/i;
const COLLECT = /\bcollect|\brecord|\bcaptur|\bobtain|\bself[- ]report|\bdocument(?:ed)?\b|\bdemographic/i;
/** Background prose about race or ethnicity ("disparities are well documented") is not a collection statement. */
const BACKGROUND = /\bdisparit|\bdifferences?\b|\bwell[- ]documented\b|\b(?:have|has)\s+been\s+(?:documented|recorded|reported|observed|shown)\b|\bprevious(?:ly)?\b|\bprior\s+stud|\bliterature\b/i;
const NEGATED = /\bnot\s+(?:be\s+)?(?:collect|record|captur|ask|obtain|report)|\bregardless\b|\birrespective\b|\bany\s+race\b|\bany\s+ethnicit/i;

const clean = (s: string): string => s.replace(/\s+/g, " ").trim();
/** The protocol's words from the heading just before `from` (e.g. "Race:") to `to`, whole words only. */
function quoteFrom(text: string, from: number, to: number): string {
  const leadStart = Math.max(0, from - 24);
  const head = /(?:race|ethnicity|ethnic\s+(?:group|origin|category))\s*[:(]?\s*$/i.exec(text.slice(leadStart, from));
  const start = head ? leadStart + head.index : from;
  return clean(text.slice(start, to)).slice(0, 240);
}
function sentences(text: string): string[] {
  return text.replace(/\r/g, "").split(/(?<=[.!?])\s+|\n{2,}/).map(clean).filter(Boolean);
}

function raceList(text: string): RaceEvidence | null {
  // Every place one category occurs; a window starting there holding three or more distinct categories.
  for (let start = 0; start < text.length; ) {
    let first = -1;
    for (const c of OMB_RACE) {
      const m = c.re.exec(text.slice(start));
      if (m && (first < 0 || start + m.index < first)) first = start + m.index;
    }
    if (first < 0) return null;
    const window = text.slice(first, first + SPAN);
    const hits = OMB_RACE.map((c) => ({ m: c.re.exec(window) })).filter((h) => h.m).sort((a, b) => a.m!.index - b.m!.index);
    if (hits.length >= 3) {
      const last = hits[hits.length - 1].m!;
      return { quote: quoteFrom(text, first, first + last.index + last[0].length), categories: hits.map((h) => clean(h.m![0])) };
    }
    start = first + 1;
  }
  return null;
}

function ethnicityList(text: string): RaceEvidence | null {
  const not = /Not Hispanic (?:or|and|\/) Latin[oa]/i.exec(text);
  if (!not) return null;
  const before = text.slice(Math.max(0, not.index - SPAN), not.index);
  const yes = [...before.matchAll(/(?<!Not )Hispanic (?:or|and|\/) Latin[oa]/gi)].pop();
  if (!yes || yes.index === undefined) return null;
  const from = Math.max(0, not.index - SPAN) + yes.index;
  return { quote: quoteFrom(text, from, not.index + not[0].length), categories: [clean(yes[0]), clean(not[0])] };
}

function collectionSentence(text: string, topic: RaceTopic): RaceEvidence | null {
  const word = topic === "race" ? RACE_WORD : ETH_WORD;
  for (const s of sentences(text)) {
    if (!word.test(s) || !COLLECT.test(s) || NEGATED.test(s) || BACKGROUND.test(s)) continue;
    return { quote: s.slice(0, 240), categories: [] };
  }
  return null;
}

/** The protocol text's evidence that race (or ethnicity) is collected, or null. */
export function raceEthnicityInText(protocolText: string | null | undefined, topic: RaceTopic): RaceEvidence | null {
  const text = String(protocolText ?? "");
  if (!text.trim()) return null;
  // A whole-study statement that the item is NOT collected outranks a list (the caller also asks
  // crfProtocolReading.demographicNotCollected; this keeps the function safe on its own).
  const word = topic === "race" ? RACE_WORD : ETH_WORD;
  if (sentences(text).some((s) => word.test(s) && /\bnot\s+(?:be\s+)?(?:collect|record|captur|obtain)/i.test(s))) return null;
  return (topic === "race" ? raceList(text) : ethnicityList(text)) ?? collectionSentence(text, topic);
}

/**
 * A quote of the protocol naming the National Institutes of Health (or an NIH grant) as the
 * study's FUNDER, or null. A funding word or a grant number must sit next to it, so a scale
 * named after NIH ("NIH Stroke Scale", "NIH Toolbox") does not count.
 */
export function nihFunding(protocolText: string | null | undefined): string | null {
  const text = String(protocolText ?? "");
  // The acronym only in capitals and never inside a web address ("grants.nih.gov" is a link, not a funder).
  const NIH = /[Nn][Aa][Tt][Ii][Oo][Nn][Aa][Ll] [Ii][Nn][Ss][Tt][Ii][Tt][Uu][Tt][Ee][Ss]? [Oo][Ff] [Hh][Ee][Aa][Ll][Tt][Hh](?!\s+(?:Stroke|Toolbox|PROMIS|Scale))|(?<![./\w])NIH\b(?!\s+(?:Stroke|Toolbox|PROMIS|Scale))/g;
  const FUND = /\bfund(?:ed|ing|s)?\b|\bgrants?\b|\baward\b|\bsponsor|\bsupported\b/i;
  const GRANT_NO = /\b(?:R|K|U|P|F|T)\d{2}\s?[A-Z]{2}\d{5,6}\b/;
  for (const m of text.matchAll(NIH)) {
    const i = m.index ?? 0;
    const near = text.slice(Math.max(0, i - 80), i + m[0].length + 80);
    if (FUND.test(near) || GRANT_NO.test(near)) return clean(near).slice(0, 200);
  }
  return null;
}

/** Why a US-standard race/ethnicity question is expected, in plain words, or null. */
export function raceEthnicityExpected(protocolText: string | null | undefined, topic: RaceTopic): { why: string; categories: string[] } | null {
  const ev = raceEthnicityInText(protocolText, topic);
  if (ev) return { why: `the protocol text names it: "${ev.quote}"`, categories: ev.categories };
  const nih = nihFunding(protocolText);
  if (nih) return { why: `the study is funded by the National Institutes of Health, which requires enrolment to be reported by race and ethnicity ("${nih}")`, categories: [] };
  return null;
}
